import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { DrawerLayout } from '@/layouts/DrawerLayout';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Button, Box, Container, Theme, useMediaQuery, Typography, Card, CardHeader, CardContent } from '@mui/material';
import Grid from '@mui/material/Grid2';
import DrawerHeader from '@/components/Organisms/BasicDrawer/DrawerHeader.component';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSuspenseQuery } from '@tanstack/react-query';
import TextBox from '@/components/Molecules/Textbox/Textbox.component';
import { LoadingButton } from '@mui/lab';
import { Copy, Trash2Icon, Plus } from 'lucide-react';
import ConfirmModal from '@/components/Molecules/Modal/ConfirmModal.component';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { createRoleIdQueryOptions } from '@/services/rolesPermissions/rolesPermission.options';
import CheckboxElement from '@/components/Molecules/Checkbox/CheckboxElement.component';
import useRoleMutation from '@/services/rolesPermissions/rolesPermission.mutations';

dayjs.extend(utc);

const permissionValuesSchema = z.object({
  role: z.object({
    roleId: z.number(),
    rolename: z.string(),
    roledesc: z.string(),
    ScopeTypeName: z.string(),
    ScopeTypeId: z.number(),
    PermssionSet: z.number(),
  }),
  permissions: z.object({
    'User Manager': z.boolean(),
    'Customer Manager': z.boolean(),
    'System Manager': z.boolean(),
    'Report Executor': z.boolean(),
    'Ticket Creator': z.boolean(),
    'Ticket Deleter': z.boolean(),
    'Ticket Editor': z.boolean(),
    'Ticket Mover': z.boolean(),
    'Ticket Assign-other': z.boolean(),
    'All-seer': z.boolean(),
    'Ticket Staff': z.boolean(),
    'Notification Author': z.boolean(),
    'Mass Update Manager': z.boolean(),
    'Customer Administrator': z.boolean()
  })
});

type PermissionValues = z.infer<typeof permissionValuesSchema>;

const PERMISSIONS = [
  {
    'User Manager': {
      name: 'User Manager',
      description: 'The permission allows the user to modify any and all users\'s information. Having this permissions will include the ability to see all users regardless of their customer.',
      value: false
    }
  },
  {
    'Customer Manager': {
      name: 'Customer Manager',
      description: 'Can modify customers as well as view all customers not associated with this user',
      value: false
    }
  },
  {
    'System Manager': {
      name: 'System Manager',
      description: 'Can modify statuses, types, reports, custom fields, roles.',
      value: false
    }
  },
  {
    'Report Executor': {
      name: 'Report Executor',
      description: 'Can execute reports with limited access to tokens.',
      value: false
    }
  },
  {
    'Ticket Creator': {
      name: 'Ticket Creator',
      description: 'Can create tickets.',
      value: false
    }
  },
  {
    'Ticket Deleter': {
      name: 'Ticket Deleter',
      description: 'Can delete tickets.',
      value: false
    }
  },
  {
    'Ticket Editor': {
      name: 'Ticket Editor',
      description: 'Can edit tickets, not including status nor assignee.',
      value: false
    }
  },
  {
    'Ticket Mover': {
      name: 'Ticket Mover',
      description: 'Extends the Ticket Editor permission by allowing the user to change the status of the ticket.',
      value: false
    }
  },
  {
    'Ticket Assign-other': {
      name: 'Ticket Assign-other',
      description: 'Extends the Ticket Editor permission by allowing the user to assign tickets to other people outside of themselves.',
      value: false
    }
  },
  {
    'All-seer': {
      name: 'All-seer',
      description: 'Anyone with this permission is implicitly linked to all customers. This in turn gives them access to all customers (existing and future), statuses (existing and future), and tickets (existing and future, regardless of assignee).',
      value: false
    }
  },
  {
    'Ticket Staff': {
      name: 'Ticket Staff',
      description: 'This allows the ability to see tickets that are not assigned to the user. Without this permission, the user will only see tickets that are assigned to them.',
      value: false
    }
  },
  {
    'Notification Author': {
      name: 'Notification Author',
      description: 'If true, user can send notifications to other users. This permission is not implemented to be used on normal users.',
      value: false
    }
  },
  {
    'Mass Update Manager': {
      name: 'Mass Update Manager',
      description: 'Can create, delete, and execute mass update actions.',
      value: false
    }
  },
  {
    'Customer Administrator': {
      name: 'Customer Administrator',
      description: 'Grant administrative functions to the user with visibility restricted to the user\'s assigned customer(s).',
      value: false
    }
  }
]

const initialPermissions = {
  'User Manager': false,
  'Customer Manager': false,
  'System Manager': false,
  'Report Executor': false,
  'Ticket Creator': false,
  'Ticket Deleter': false,
  'Ticket Editor': false,
  'Ticket Mover': false,
  'Ticket Assign-other': false,
  'All-seer': false,
  'Ticket Staff': false,
  'Notification Author': false,
  'Mass Update Manager': false,
  'Customer Administrator': false
}

const initialRole = {
  roleId: 0,
  rolename: '',
  roledesc: '',
  ScopeTypeName: '',
  ScopeTypeId: 0,
  PermssionSet: 0,
}



const RolesPermissionsDrawer = () => {
  const navigate = useNavigate();
  const params = useParams({ from: '/_auth/permissions/$roleId' });
  const { variant } = useSearch({ from: '/_auth/permissions/$roleId' })
  const [activeStep, setActiveStep] = useState(0);

  const role = useSuspenseQuery(createRoleIdQueryOptions(params.roleId))

  const initialValues = {
    role: role?.data?.Role || initialRole,
    permissions: role?.data?.Permissions || initialPermissions
  }

  const methods = useForm<any>({
    mode: 'all',
    // defaultValues: initialValues,
    // shouldUnregister: false,
    // resolver: zodResolver(permissionValuesSchema)
  });

  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  const { createRoleMutation, updateRoleMutation, isUpdatingCreateRole, isUpdatingUpdateRole, deleteRoleMutation } = useRoleMutation()

  const [isOpen, setIsOpen] = useState(false)
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Open drawer when component mounts
  useEffect(() => {
    setIsOpen(true)
  }, [])

  const handleClose = () => {
    setIsOpen(false)
    // Wait for animation to complete before navigating
    setTimeout(() => {
      navigate({ to: '/permissions' })
    }, 300) // Match this with MUI's transition duration
  }

  const handleCloseDrawer = () => {
    handleClose()
    setActiveStep(0);
    // methods.reset(editCustomerValues2);
    navigate({ to: '/permissions' });
  };

  const handleSubmit: SubmitHandler<any> = (formValues: any) => {
    console.log('formValues : ', formValues);

    if (variant === 'edit') {
      console.log('edit')
      updateRoleMutation.mutate({
        actionType: 'updateRole',
        payload: formValues
      })
    } else {
      console.log('new')
      createRoleMutation.mutate({
        actionType: 'createRole',
        payload: formValues
      })
    }
    return undefined
  }

  const handleComplete = () => {
    console.log('handleComplete')
    console.log(methods.getValues())
  }

  const handleDelete = () => {
    console.log('handleDelete')
    setIsDeleting(true);
    deleteRoleMutation.mutate({
      actionType: 'deleteRole',
      payload: {
        roleid: params.roleId
      }
    })
  };

  useEffect(() => {
    if (variant === 'edit') {
      console.log('edit')
      methods.reset({
        role: role.data.Role,
        permissions: role.data.Permissions
      })
      console.log('methods.getValues() : ', methods.getValues())
    }
  }, [variant])


  return (
    <DrawerLayout
      open={isOpen}
      onClose={handleCloseDrawer}
      anchor="right"
      keepMounted
      width={'80%'}
      Header={
        <DrawerHeader
          close={handleCloseDrawer}
          title={variant === 'edit' ? 'Edit Role & Permissions' : 'New Role & Permissions'}
        />
      }
    >
      <FormProvider {...methods}>
        {/* @ts-ignore */}
        <Container maxWidth='xl' component='form' onSubmit={methods.handleSubmit(handleSubmit)} sx={{
          overflowY: 'auto',
          mt: 5,
          height: '98%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          mb: 4,
        }}>

          <Grid size={12} display="flex" flexDirection="column" flexWrap="wrap" >
            <Grid size={12} container rowGap={4} >

              <Grid size={{ xs: 12, md: 6 }} sx={{
                p: 2
              }}>
                <TextBox
                  name='role.rolename'
                  fullWidth
                  label='Name'
                  required
                  size='small'
                />

              </Grid>
              <Grid size={{ xs: 12, md: 6 }} sx={{
                p: 2
              }}>
                <TextBox
                  name='role.roledesc'
                  fullWidth
                  label='Description'
                  multiline
                  rows={4}
                  size='small'
                />
              </Grid>
            </Grid>
            <Grid size={12} container rowGap={4} >
              {PERMISSIONS.map((permission, index) => {
                const key = Object.keys(permission)[0] as keyof PermissionValues['permissions'];
                const permissionName = key;
                const permissionData = permission[permissionName];

                if (!permissionData) {
                  return null;
                }

                console.log('permissionData : ', permissionData)

                return (
                  <Grid size={{ xs: 12, md: 6, lg: 4, xl: 2 }} key={index} sx={{ p: 2 }}>
                    <Card sx={{ height: '100%', minWidth: '200px', }}>
                      <CardHeader
                        title={(
                          <CheckboxElement
                            control={methods.control}
                            id={`permissions.${permissionName}`}
                            name={`permissions.${permissionName}`}
                            label={permissionData.name}
                            sx={{
                              fontWeight: 'bold',
                              color: 'primary.main',
                            }}
                          />
                        )}
                      />
                      <CardContent>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {permissionData.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Grid>

          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              backgroundColor: (theme: Theme) => theme.palette.background.paper,
              borderTop: '1px solid',
              borderColor: (theme: Theme) => theme.palette.divider,
              p: 5,
              display: 'flex',
              justifyContent: 'space-between',
              gap: '1rem'
            }}>
            <LoadingButton
              loading={isDeleting}
              color="error"
              startIcon={<Trash2Icon />}
              size='small'
              onClick={() => setOpenModal(true)}>
              Delete
            </LoadingButton>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={handleCloseDrawer} size='small'>
                Cancel & Close
              </Button>
              {openModal && (
                <ConfirmModal
                  open={openModal}
                  setOpen={setOpenModal}
                  title="Are you sure?"
                  content="This action will permanently delete this role."
                  onSuccess={handleDelete}
                  onClose={() => setOpenModal(false)}
                  cancelButtonText="Cancel"
                  confirmButtonText="Delete"
                />
              )}
              <LoadingButton loading={isUpdatingUpdateRole['updateRole'] || isUpdatingCreateRole['createRole']} variant="contained" type="submit" size='small'>
                Save
              </LoadingButton>
            </Box>
          </Box>
        </Container >

      </FormProvider >
    </DrawerLayout >
  )
}

export default RolesPermissionsDrawer